import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { configureApiClient } from '../lib/api-client';
import { useAuthStore } from './auth-store';
import { fetchMe, silentRefresh } from './auth-api';
import { useProjectStore } from '../project/project-store';

/**
 * Wires the api-client hooks to the auth store + navigation, and runs a
 * silent-refresh probe on mount so a warm reload keeps the session.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    configureApiClient({
      getAccessToken: () => useAuthStore.getState().accessToken,
      setAccessToken: (token) => useAuthStore.getState().setAccessToken(token),
      getProjectId: () => useProjectStore.getState().projectId,
      onAuthLost: () => {
        useAuthStore.getState().clear();
        useProjectStore.getState().clear();
        // Drop the previous user's cached queries so a re-login on the same
        // tab can't briefly surface stale cross-user data (FE-B2).
        queryClient.clear();
        navigate('/login', { replace: true });
      },
    });

    let cancelled = false;
    (async () => {
      const token = await silentRefresh();
      if (cancelled) return;
      if (token) {
        useAuthStore.getState().setAccessToken(token);
        try {
          const user = await fetchMe();
          useAuthStore.getState().setSession(user, token);
        } catch {
          useAuthStore.getState().clear();
        }
      }
      useAuthStore.getState().setBootstrapped(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, queryClient]);

  return <>{children}</>;
}
